import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Clock, Save, Pencil, Check, Sun, Moon, CalendarRange } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import { companyApi } from '../../../lib/api';

const DAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

const HOURS = [];
for (let hour = 6; hour <= 21; hour++) {
  for (let minute = 0; minute < 60; minute += 15) {
    if (hour === 21 && minute > 0) break;
    const hourStr = hour.toString().padStart(2, '0');
    const minStr = minute.toString().padStart(2, '0');
    HOURS.push({
      value: `${hourStr}:${minStr}`,
      label: `${hour}h${minute === 0 ? '00' : minStr}`,
    });
  }
}

const PERIODS = [
  {
    key: 'morning',
    label: 'Matin',
    icon: Sun,
    tone: 'amber',
    description: 'Demi-journée matin',
    defaults: { start: '08:00', end: '12:00' },
  },
  {
    key: 'afternoon',
    label: 'Après-midi',
    icon: Moon,
    tone: 'indigo',
    description: 'Demi-journée après-midi',
    defaults: { start: '14:00', end: '18:00' },
  },
  {
    key: 'full_day',
    label: 'Journée complète',
    icon: CalendarRange,
    tone: 'emerald',
    description: 'Journée continue (avec ou sans pause interne)',
    defaults: { start: '08:00', end: '18:00' },
  },
];

const toneClasses = {
  amber: {
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: 'text-amber-600',
    pill: 'bg-amber-100',
  },
  indigo: {
    badge: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    icon: 'text-indigo-600',
    pill: 'bg-indigo-100',
  },
  emerald: {
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    icon: 'text-emerald-600',
    pill: 'bg-emerald-100',
  },
};

const blankSlot = (dayValue) => ({
  day_of_week: dayValue,
  start_time: '08:00',
  end_time: '18:00',
  is_active: false,
  morning: { start: '08:00', end: '12:00', active: false },
  afternoon: { start: '14:00', end: '18:00', active: false },
  full_day: { start: '08:00', end: '18:00', active: false },
});

const recomputeAggregate = (slot) => {
  const periods = [slot.morning, slot.afternoon, slot.full_day].filter((p) => p.active);
  if (periods.length === 0) {
    return { ...slot, is_active: false };
  }
  const start = periods.reduce((min, p) => (p.start < min ? p.start : min), '23:59');
  const end = periods.reduce((max, p) => (p.end > max ? p.end : max), '00:00');
  return { ...slot, is_active: true, start_time: start, end_time: end };
};

export function WorkHoursPage() {
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [editSlot, setEditSlot] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = async () => {
    try {
      const res = await companyApi.getWorkHours();
      const fetched = res.data.slots || [];
      // Ensure 1 slot per day (consolidated by backend), fill missing days
      const byDay = {};
      fetched.forEach((s) => {
        byDay[s.day_of_week] = s;
      });
      const normalized = DAYS.map((d) => byDay[d.value] || blankSlot(d.value));
      setSlots(normalized);
    } catch (error) {
      console.error('Error fetching work hours:', error);
      toast.error('Erreur lors du chargement des horaires');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openEditDay = (dayValue) => {
    const slot = slots.find((s) => s.day_of_week === dayValue) || blankSlot(dayValue);
    setEditingDay(dayValue);
    // Deep clone for editing
    setEditSlot({
      ...slot,
      morning: { ...slot.morning },
      afternoon: { ...slot.afternoon },
      full_day: { ...slot.full_day },
    });
    setIsEditOpen(true);
  };

  const togglePeriod = (periodKey, active) => {
    setEditSlot((prev) => {
      if (!prev) return prev;
      // 3 périodes indépendantes (Matin / Après-midi / Journée complète) — pas de mutualité
      return { ...prev, [periodKey]: { ...prev[periodKey], active } };
    });
  };

  const updatePeriodTime = (periodKey, field, value) => {
    setEditSlot((prev) => ({
      ...prev,
      [periodKey]: { ...prev[periodKey], [field]: value },
    }));
  };

  const validateEditSlot = () => {
    const periods = ['morning', 'afternoon', 'full_day'];
    for (const k of periods) {
      const p = editSlot[k];
      if (p.active && p.start >= p.end) {
        toast.error(`L'heure de fin doit être après l'heure de début (${PERIODS.find((x) => x.key === k).label})`);
        return false;
      }
    }
    if (
      editSlot.morning.active &&
      editSlot.afternoon.active &&
      editSlot.morning.end > editSlot.afternoon.start
    ) {
      toast.error('La fin du matin doit être avant le début de l\'après-midi');
      return false;
    }
    return true;
  };

  const saveEditedDay = () => {
    if (!validateEditSlot()) return;
    const updated = recomputeAggregate(editSlot);
    setSlots((prev) => prev.map((s) => (s.day_of_week === editingDay ? updated : s)));
    setHasChanges(true);
    setIsEditOpen(false);
    toast.success('Modifications enregistrées localement');
  };

  const toggleDayActive = (dayValue) => {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.day_of_week !== dayValue) return s;
        const isActive = s.is_active;
        if (isActive) {
          // Disable everything
          return recomputeAggregate({
            ...s,
            morning: { ...s.morning, active: false },
            afternoon: { ...s.afternoon, active: false },
            full_day: { ...s.full_day, active: false },
          });
        }
        // Enable a sensible default (full_day)
        return recomputeAggregate({
          ...s,
          full_day: { ...s.full_day, active: true },
        });
      }),
    );
    setHasChanges(true);
  };

  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Only send slots with at least one period for clarity; backend recomputes is_active
      const payload = slots.map((s) => ({
        day_of_week: s.day_of_week,
        start_time: s.start_time,
        end_time: s.end_time,
        is_active: s.is_active,
        morning: s.morning,
        afternoon: s.afternoon,
        full_day: s.full_day,
      }));
      await companyApi.bulkUpdateWorkHours(payload);
      toast.success('Horaires de travail enregistrés');
      setHasChanges(false);
      fetchData();
    } catch (error) {
      console.error('Error saving work hours:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderPeriodBadge = (slot, periodKey) => {
    const period = slot[periodKey];
    if (!period?.active) return null;
    const config = PERIODS.find((p) => p.key === periodKey);
    const tone = toneClasses[config.tone];
    const Icon = config.icon;
    return (
      <Badge
        key={periodKey}
        variant="outline"
        className={`${tone.badge} gap-1.5 px-2.5 py-1`}
        data-testid={`day-${slot.day_of_week}-period-${periodKey}`}
      >
        <Icon className="w-3 h-3" />
        <span className="font-medium">{config.label}</span>
        <span className="opacity-70">{period.start}–{period.end}</span>
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="work-hours-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Horaires de travail</h1>
          <p className="text-muted-foreground">
            Définissez vos plages disponibles pour le matin, l'après-midi ou la journée complète
          </p>
        </div>
        {hasChanges && (
          <Button
            onClick={saveAllChanges}
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700"
            data-testid="save-work-hours-btn"
          >
            {isSaving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer les modifications
          </Button>
        )}
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Aligné avec vos types de créneaux</p>
              <p className="text-blue-700">
                Définissez les horaires <strong>du matin</strong>, <strong>de l'après-midi</strong> ou
                de la <strong>journée complète</strong>. Ces plages servent à proposer les bons
                créneaux dans vos widgets : demi-journée matin, demi-journée après-midi ou journée
                complète. Si vous activez "Journée complète", le matin et l'après-midi sont
                automatiquement désactivés (et inversement).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Days Grid */}
      <div className="grid gap-3">
        {DAYS.map((day) => {
          const slot = slots.find((s) => s.day_of_week === day.value) || blankSlot(day.value);
          const isActive = slot.is_active;
          return (
            <Card
              key={day.value}
              className={`transition-all ${!isActive ? 'opacity-60' : ''}`}
              data-testid={`day-card-${day.value}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <Switch
                      checked={isActive}
                      onCheckedChange={() => toggleDayActive(day.value)}
                      data-testid={`toggle-day-${day.value}`}
                    />
                    <div className="min-w-[100px]">
                      <span className={`font-medium ${!isActive ? 'text-muted-foreground' : ''}`}>
                        {day.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {isActive ? (
                        <>
                          {renderPeriodBadge(slot, 'morning')}
                          {renderPeriodBadge(slot, 'afternoon')}
                          {renderPeriodBadge(slot, 'full_day')}
                          {!slot.morning.active && !slot.afternoon.active && !slot.full_day.active && (
                            <span className="text-sm text-muted-foreground italic">
                              Aucune plage active — cliquez sur ✏️ pour configurer
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">Fermé</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDay(day.value)}
                    data-testid={`edit-day-${day.value}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Horaires – {DAYS.find((d) => d.value === editingDay)?.label}
            </DialogTitle>
            <DialogDescription>
              Activez les plages utilisées pour ce jour. Les 3 périodes peuvent être actives
              simultanément.
            </DialogDescription>
          </DialogHeader>

          {editSlot && (
            <div className="space-y-3 py-2">
              {PERIODS.map((cfg) => {
                const period = editSlot[cfg.key];
                const tone = toneClasses[cfg.tone];
                const Icon = cfg.icon;
                return (
                  <div
                    key={cfg.key}
                    className={`rounded-xl border p-4 transition-all ${
                      period.active ? 'border-slate-300 bg-white' : 'border-dashed border-slate-200 bg-slate-50/50'
                    }`}
                    data-testid={`edit-period-${cfg.key}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-9 h-9 rounded-lg ${tone.pill} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${tone.icon}`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{cfg.label}</div>
                        <div className="text-xs text-muted-foreground">{cfg.description}</div>
                      </div>
                      <Switch
                        checked={period.active}
                        onCheckedChange={(checked) => togglePeriod(cfg.key, checked)}
                        data-testid={`toggle-period-${cfg.key}`}
                      />
                    </div>
                    {period.active && (
                      <div className="grid grid-cols-2 gap-3 ml-12">
                        <div>
                          <Label className="text-xs text-muted-foreground">Début</Label>
                          <Select
                            value={period.start}
                            onValueChange={(v) => updatePeriodTime(cfg.key, 'start', v)}
                          >
                            <SelectTrigger data-testid={`period-${cfg.key}-start`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {HOURS.map((h) => (
                                <SelectItem key={h.value} value={h.value}>
                                  {h.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Fin</Label>
                          <Select
                            value={period.end}
                            onValueChange={(v) => updatePeriodTime(cfg.key, 'end', v)}
                          >
                            <SelectTrigger data-testid={`period-${cfg.key}-end`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {HOURS.map((h) => (
                                <SelectItem key={h.value} value={h.value}>
                                  {h.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveEditedDay} data-testid="confirm-edit-day-btn">
              <Check className="w-4 h-4 mr-2" />
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
